import { RemoveCircleOutline, Upload } from '@mui/icons-material';
import {
    Button, IconButton, Link, Paper, Tooltip, Typography
} from '@mui/material';
import clsx from 'clsx';
import { DragEvent, useState } from 'react';

import styles from './FileInput.module.scss';


interface FileInputProps {
    title: 'file' | 'document'
    acceptedFileTypes: string[];
    onAddFiles: (newFiles: File[]) => void;
    files?: LinkedFile[];
    onRemoveFile?: (removedFile: File) => void;
    compact?: boolean;
    className?: string;
    hideDropZone?: boolean;
    disabled?: boolean;
    single?: boolean; // TODO post-demo change this to multiple & default to true
}

export class LinkedFile extends File {
    url?: string;

    constructor(fileBits: BlobPart[], fileName: string, options?: FilePropertyBag | undefined, url?: string) {
        super(fileBits, fileName, options);
        this.url = url;
    }
}

export function FileInput({
    title, files, acceptedFileTypes, onAddFiles, onRemoveFile, compact, className, hideDropZone, disabled, single
}: FileInputProps) {
    const [ inDropZone, setInDropZone ] = useState(false);

    function isValidFile(file: File, existingFiles: string[]) {
        const fileType = file.name.split('.')[1] ?? '';

        const isUnique = !existingFiles.includes(file.name);
        const isSupported = acceptedFileTypes.includes(fileType);

        return isUnique && isSupported;
    }

    function addFiles(fileList: FileList | null) {
        if (!fileList) {
            return;
        }

        const existingFiles = files?.map(file => file.name) || [];
        const newFiles = [ ...fileList ].filter(file => isValidFile(file, existingFiles));

        onAddFiles(newFiles);
    }

    function handleDragEvent(event: DragEvent<HTMLDivElement>) {
        event.preventDefault();
        event.stopPropagation();

        if (event.type === 'dragover') {
            event.dataTransfer.dropEffect = 'copy';
        } else {
            if (event.type === 'drop') {
                addFiles(event.dataTransfer.files);
            }

            setInDropZone(event.type === 'dragenter');
        }
    }

    return (
        <div
            className={clsx(styles.fileInputContainer, className, {
                [styles.compact]: compact
            })}
        >
            <div
                className={clsx(styles.dropZone, {
                    [styles.dropZoneActive]: inDropZone,
                    [styles.compact]: compact,
                    [styles.hidden]: hideDropZone
                })}
                onDrop={handleDragEvent}
                onDragOver={handleDragEvent}
                onDragEnter={handleDragEvent}
                onDragLeave={handleDragEvent}
            >
                <Upload
                    fontSize="large"
                    color="secondary"
                />

                <Typography
                    align="center"
                    color="textSecondary"
                    variant={compact ? 'body2' : undefined}
                >
                    {`Drag and drop ${title}s to upload`}
                </Typography>

                <label htmlFor="file-upload">
                    <input
                        id="file-upload"
                        className={styles.hiddenFileInput}
                        type="file"
                        onChange={e => {
                            addFiles(e.target.files);
                            e.target.value = ''; // Clear the value so onChange fires the next time a file is selected
                        }}
                        multiple={!single}
                    />

                    <Button
                        component="span"
                        size={compact ? 'small' : undefined}
                        disabled={disabled}
                    >
                        {`Browse ${title}s instead`}
                    </Button>
                </label>
            </div>

            {files && (
                <Paper
                    variant="outlined"
                    className={clsx(styles.filesContainer, {
                        [styles.noUploads]: !files.length,
                        [styles.compact]: compact
                    })}
                >
                    {files.length ? (
                        <>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                            >
                                {`Attached ${title}s`}
                            </Typography>

                            {files.map(file => (
                                <Paper
                                    className={clsx(styles.fileCard, {
                                        [styles.compact]: compact
                                    })}
                                    variant="outlined"
                                    key={file.name}
                                >
                                    <Typography variant={compact ? 'body2' : undefined}>
                                        {file.url ? (
                                            <Link
                                                href={file.url}
                                                rel="noreferrer noopener"
                                                target="_blank"
                                            >
                                                {file.name}
                                            </Link>
                                        ) : file.name}
                                    </Typography>

                                    <Tooltip title="Remove file">
                                        <IconButton
                                            edge="end"
                                            size={compact ? 'small' : undefined}
                                            onClick={() => onRemoveFile?.(file)}
                                        >
                                            <RemoveCircleOutline
                                                color="error"
                                                fontSize={compact ? 'small' : undefined}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </Paper>
                            ))}
                        </>
                    ) : (
                        <Typography
                            variant="caption"
                            color="textSecondary"
                        >
                            {`No attached ${title}s`}
                        </Typography>
                    )}
                </Paper>
            )}
        </div>
    );
}
